import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import {
  UploadArea,
  UploadIcon,
  UploadText,
  UploadMainText,
  SupportedFormats,
  FileSizeLimit,
  SampleFileLink,
  HiddenFileInput,
} from './UploadZone.styled';

interface UploadZoneProps {
  onFilesSelected: (files: FileList) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  disabled?: boolean;
  sampleFile?: string;
  sampleFileText?: string;
  onDownloadClick?: () => void;
  children?: React.ReactNode;
}

export default function UploadZone({
  onFilesSelected,
  maxFiles = 20,
  maxFileSize = 20,
  acceptedTypes = ['.jpeg', '.jpg', '.png', '.pdf'],
  disabled = false,
  sampleFile,
  sampleFileText = "Download sample file",
  onDownloadClick,
  children,
}: UploadZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { error } = useToast();

  const validateFiles = (files: FileList): boolean => {
    const maxSizeInBytes = maxFileSize * 1024 * 1024;
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSizeInBytes) {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      const fileList = invalidFiles.length === 1 
        ? invalidFiles[0] 
        : invalidFiles.length === 2
        ? invalidFiles.join(' and ')
        : `${invalidFiles.slice(0, -1).join(', ')}, and ${invalidFiles[invalidFiles.length - 1]}`;
      
      error(
        `File size exceeds limit`,
        {
          description: `${fileList} ${invalidFiles.length === 1 ? 'exceeds' : 'exceed'} the maximum file size of ${maxFileSize}MB.`,
          duration: 5000,
        }
      );
      return false;
    }

    return true;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      if (validateFiles(files)) {
        onFilesSelected(files);
      }
      // Clear the file input to allow re-upload
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (validateFiles(files)) {
        onFilesSelected(files);
      }
    }
    // Clear the file input to allow re-upload
    e.target.value = '';
  };

  const handleBrowseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleUploadAreaClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const formatAcceptedTypes = () => {
    return acceptedTypes
      .map(type => type.replace('.', '').toUpperCase())
      .join(', ');
  };

  const handleDownloadSample = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDownloadClick) {
      onDownloadClick();
    }
  };

  return (
    <>
      <UploadArea
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleUploadAreaClick}
        style={{
          opacity: disabled ? 0.7 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {children || (
          <>
            <UploadIcon>
              <Upload size={24} />
            </UploadIcon>

            <UploadText>
              <UploadMainText>
                Upload File: drop here or{' '}
                <span className="browse-link" onClick={handleBrowseClick}>
                  browse
                </span>
              </UploadMainText>
            </UploadText>

            <SupportedFormats>
              Supports: {formatAcceptedTypes()}
            </SupportedFormats>

            <FileSizeLimit>
              Upload up to {maxFiles} files of size {maxFileSize}MB each
            </FileSizeLimit>

            {(sampleFile || onDownloadClick) && (
              <SampleFileLink 
                href={sampleFile}
                onClick={handleDownloadSample}
                target={sampleFile ? '_blank' : undefined}
                rel={sampleFile ? 'noopener noreferrer' : undefined}
              >
                {sampleFileText}
              </SampleFileLink>
            )}
          </>
        )}
      </UploadArea>

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(',')}
        onChange={handleFileInputChange}
      />
    </>
  );
}
